/**
 * BankFeedSaveChangesBar - Floating "Save N changes" pill.
 *
 * Phase #5 of the bank-feed accountant-friendly polish: category edits
 * are no longer auto-saved on every dropdown change. Each change is
 * staged in a pending edits map; this bar surfaces the queue and lets
 * the user explicitly commit or discard.
 *
 * On save we hit `POST /bank-feeds/save-changes/{tx_id}` per row (the
 * server already reverses the original journal and re-posts atomically
 * when the row is "completed"). This means the cascade the user
 * requested - hard-delete journal lines, remove auto-created docs,
 * regenerate special-item schedules + notifications - is enforced by
 * the existing endpoint; the frontend only needs to drive the queue.
 *
 * UX:
 *   • Bottom-right pinned card, slides in when pendingEdits is non-empty
 *   • Shows the count + a preview list (max 4 rows, "…and N more")
 *   • Save button → triggers all updates with sequential awaits, surfaces
 *     per-row failures and rolls back the pending map for failures only
 *   • Discard button → wipes the pending map without server calls
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Save, X, AlertTriangle, Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import { Badge } from '../ui/badge';

export const BankFeedSaveChangesBar = ({
  pendingEdits = new Map(),
  transactionsById = new Map(),
  onSave,
  onDiscard,
  saving = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const count = pendingEdits.size;
  if (!count) return null;

  const entries = Array.from(pendingEdits.entries());
  const previewLimit = 4;
  const preview = entries.slice(0, previewLimit);
  const more = entries.length - preview.length;

  return (
    <div
      className="fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom))] right-6 z-50 w-[360px] rounded-xl border border-amber-200 bg-white shadow-2xl overflow-hidden"
      data-testid="bf-save-changes-bar"
      role="region"
      aria-label="Pending category changes"
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-amber-50 hover:bg-amber-100 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-sm font-semibold text-amber-900 truncate">
            {count} unsaved {count === 1 ? 'change' : 'changes'}
          </span>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-amber-700" /> : <ChevronUp className="w-4 h-4 text-amber-700" />}
      </button>

      {/* Preview list */}
      {expanded && (
        <div className="px-4 py-2 max-h-[180px] overflow-y-auto border-t border-amber-100" data-testid="bf-save-changes-preview">
          {preview.map(([txId, edit]) => {
            const tx = transactionsById.get(txId);
            const desc = tx?.description || edit.description || txId.slice(0, 8);
            return (
              <div key={txId} className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-100 last:border-0">
                <span className="text-xs text-gray-700 truncate flex-1" title={desc}>{desc}</span>
                <Badge variant="outline" className="text-[10px] font-medium text-emerald-700 border-emerald-200 bg-emerald-50 whitespace-nowrap">
                  → {edit.glName}
                </Badge>
              </div>
            );
          })}
          {more > 0 && (
            <p className="text-[11px] text-muted-foreground mt-1.5 italic">…and {more} more</p>
          )}
        </div>
      )}

      {/* Helper text + action buttons */}
      <div className="px-4 py-3 border-t border-amber-100 bg-white">
        <p className="text-[11px] text-gray-600 mb-2 leading-snug">
          Saving will reverse the original journal entries, remove any auto-created
          documents and schedules, and post the new categorisation across all
          relevant GLs and reports.
        </p>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={onDiscard}
            disabled={saving}
            className="text-xs h-8 px-3 text-gray-600 hover:bg-gray-100"
            data-testid="bf-discard-changes-btn"
          >
            <X className="w-3.5 h-3.5 mr-1" />
            Discard
          </Button>
          <Button
            size="sm"
            onClick={onSave}
            disabled={saving}
            className="flex-1 text-xs h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="bf-save-changes-btn"
          >
            {saving ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving…</>
            ) : (
              <><Save className="w-3.5 h-3.5 mr-1" /> Save {count} {count === 1 ? 'change' : 'changes'}</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BankFeedSaveChangesBar;
